// Workspaces list, one workspace (files, versions, members), and People (member accounts).
import { useEffect, useState, type FormEvent } from 'react';
import { api, type Session } from '../api';
import { Calendar } from '../components/Calendar';
import { RoomPanel } from '../components/RoomPanel';
import { WorkspaceFiles } from '../components/WorkspaceFiles';
import { formatSize, formatWhen, type FileEntry } from '../files';
import { ex, t, T, tenant } from '../tenant';

export interface WorkspaceRow {
  id: string;
  title: string;
  updated_at: string;
  files: number;
  can_download: number;
  can_upload: number;
}

export function WorkspaceList({ admin }: { admin: boolean }) {
  const [rows, setRows] = useState<WorkspaceRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = () => api<{ workspaces: WorkspaceRow[] }>('/api/workspaces').then((r) => setRows(r.workspaces), (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    try {
      const r = await api<{ id: string }>('/api/workspaces', { body: { title: new FormData(form).get('title') } });
      location.href = `/w/${r.id}`;
    } catch (err) {
      setError((err as Error).message);
    }
  }
  return (
    <div className="list">
      {error && <div className="error">{error}</div>}
      {admin && (
        <form className="row" onSubmit={create}>
          <input name="title" className="field" style={{ flex: 1, minWidth: 200 }} placeholder={`New ${t.workspace}, e.g. ${ex.workspace}`} maxLength={120} required />
          <button className="btn primary">Create {t.workspace}</button>
        </form>
      )}
      {rows.length === 0 && <div className="muted small">{admin ? `No ${t.workspaces} yet.` : `You have not been added to any ${t.workspace} yet.`}</div>}
      {rows.map((w) => (
        <a key={w.id} className="list-item" href={`/w/${w.id}`} style={{ color: 'inherit', textDecoration: 'none' }} data-testid="workspace-row">
          <div style={{ flex: 1, minWidth: 160 }}>
            <div>{w.title}</div>
            <div className="muted small">
              {w.files} file{w.files === 1 ? '' : 's'} · updated {formatWhen(w.updated_at)}
            </div>
          </div>
          {!admin && <span className="pill dim" style={{ background: 'var(--color-accent-tint)', color: 'var(--color-accent-primary)' }}>{w.can_upload ? 'Download + upload' : w.can_download ? 'Download' : 'View list'}</span>}
        </a>
      ))}
      {admin && <Trash onRestore={() => void load()} />}
    </div>
  );
}

interface TrashData {
  days: number;
  workspaces: { id: string; title: string; deleted_at: string; deleted_by: string | null }[];
  versions: { id: string; version: number; size: number; name: string; workspace_title: string; deleted_at: string; deleted_by: string | null }[];
}

const purgeDate = (deletedAt: string, days: number) => new Date(Date.parse(deletedAt) + days * 86_400_000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

/** Deleted workspaces and file versions, restorable until the daily purge removes them. */
function Trash({ onRestore }: { onRestore: () => void }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<TrashData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => api<TrashData>('/api/trash').then(setData, (e: Error) => setError(e.message));
  useEffect(() => {
    if (open) void load();
  }, [open]);
  const restore = (path: string) =>
    api(path, { body: {} }).then(
      () => {
        void load();
        onRestore();
      },
      (e: Error) => setError(e.message),
    );
  const count = data ? data.workspaces.length + data.versions.length : null;
  return (
    <div style={{ marginTop: 8 }} data-testid="trash">
      <button className="btn ghost sm" style={{ paddingLeft: 0 }} onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} trash{count !== null ? ` (${count})` : ''}
      </button>
      {open && data && (
        <div className="list">
          <p className="muted small" style={{ margin: 0 }}>
            Deleted {t.workspaces} and files stay here for {data.days} days, then are removed for good.
          </p>
          {error && <div className="error">{error}</div>}
          {count === 0 && <div className="muted small">The trash is empty.</div>}
          {data.workspaces.map((w) => (
            <div key={w.id} className="list-item" data-testid="trash-workspace">
              <div style={{ flex: 1, minWidth: 160 }}>
                <div>
                  <strong>{w.title}</strong> <span className="tag">{t.workspace}</span>
                </div>
                <div className="muted small">
                  deleted {formatWhen(w.deleted_at)}
                  {w.deleted_by ? ` by ${w.deleted_by}` : ''} · removed for good {purgeDate(w.deleted_at, data.days)}
                </div>
              </div>
              <button className="btn sm" onClick={() => restore(`/api/workspaces/${w.id}/restore`)}>
                Restore
              </button>
            </div>
          ))}
          {data.versions.map((v) => (
            <div key={v.id} className="list-item" data-testid="trash-version">
              <div style={{ flex: 1, minWidth: 160 }}>
                <div>
                  <strong>{v.name}</strong> <span className="tag">v{v.version}</span> <span className="muted small">in {v.workspace_title}</span>
                </div>
                <div className="muted small">
                  {formatSize(v.size)} · deleted {formatWhen(v.deleted_at)}
                  {v.deleted_by ? ` by ${v.deleted_by}` : ''} · removed for good {purgeDate(v.deleted_at, data.days)}
                </div>
              </div>
              <button className="btn sm" onClick={() => restore(`/api/versions/${v.id}/restore`)}>
                Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Who changed what in one workspace (host only). */
function Activity({ id }: { id: string }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<{ at: string; actor: string; kind: string; detail: string | null }[] | null>(null);
  useEffect(() => {
    if (open) api<{ events: NonNullable<typeof events> }>(`/api/workspaces/${id}/events`).then((r) => setEvents(r.events), () => setEvents([]));
  }, [open, id]);
  return (
    <div data-testid="activity">
      <button className="btn ghost sm" style={{ paddingLeft: 0 }} onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} activity
      </button>
      {open &&
        events?.map((e, i) => (
          <div key={i} className="row small muted" style={{ marginTop: 2 }}>
            <span style={{ minWidth: 110 }}>{formatWhen(e.at)}</span>
            <span>{e.actor.replace(/^(admin|member):/, '').replace(/^guest:/, 'guest ')}</span>
            <span className="tag">{e.kind}</span>
            <span>{e.detail}</span>
          </div>
        ))}
      {open && events?.length === 0 && <div className="muted small">No activity recorded yet.</div>}
    </div>
  );
}

interface Member {
  id: string;
  username: string;
  display_name: string;
  enabled: number;
  can_download: number;
  can_upload: number;
}
interface Person {
  id: string;
  username: string;
  display_name: string;
  role?: 'member' | 'host';
  enabled: number;
  must_change_password: number;
}

export function WorkspacePage({ id, session }: { id: string; session: Session }) {
  const [data, setData] = useState<{ workspace: { id: string; title: string }; perms: { canDownload: boolean; canUpload: boolean; canManage: boolean }; files: FileEntry[]; folders: string[]; members?: Member[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [renaming, setRenaming] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = () => api<NonNullable<typeof data>>(`/api/workspaces/${id}`).then(setData, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
    if (session.role !== 'member') api<{ people: Person[] }>('/api/people').then((r) => setPeople(r.people), () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!data) return error ? <div className="error">{error}</div> : <div className="spinner" />;
  const { perms } = data;
  const nonMembers = people.filter((p) => p.enabled && !data.members?.some((m) => m.id === p.id));

  return (
    <div className="list" data-testid="workspace-page">
      <div className="row">
        {renaming ? (
          <form
            className="row"
            style={{ flex: 1 }}
            onSubmit={(e) => {
              e.preventDefault();
              const title = new FormData(e.currentTarget).get('title');
              void act(() => api(`/api/workspaces/${id}`, { method: 'PATCH', body: { title } })).then(() => setRenaming(false));
            }}
          >
            <input name="title" className="field" style={{ flex: 1 }} defaultValue={data.workspace.title} maxLength={120} autoFocus />
            <button className="btn primary sm">Save</button>
            <button type="button" className="btn ghost sm" onClick={() => setRenaming(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <>
            <h1 className="h1" style={{ flex: 1 }}>
              {data.workspace.title}
            </h1>
            {perms.canManage && (
              <button className="btn sm" onClick={() => setRenaming(true)}>
                Rename
              </button>
            )}
          </>
        )}
      </div>
      {error && <div className="error">{error}</div>}

      {perms.canManage && tenant.features.rooms && <RoomPanel workspaceId={id} />}

      <WorkspaceFiles id={id} title={data.workspace.title} files={data.files} folders={data.folders} perms={perms} reload={load} />

      {tenant.features.calendar && <Calendar workspaceId={id} canEdit={perms.canUpload} />}

      {perms.canManage && data.members && (
        <>
          <h2 className="h2" style={{ marginTop: 8 }}>
            Members
          </h2>
          <p className="muted small" style={{ margin: 0 }}>
            Members sign in with their own account and see only the {t.workspaces} they are added to. {T.guests} do not need to be members.
          </p>
          {data.members.map((m) => (
            <div key={m.id} className="list-item" data-testid="member-row">
              <div style={{ flex: 1, minWidth: 140 }}>
                {m.display_name} <span className="muted small">@{m.username}</span>
              </div>
              <label className="row small">
                <input type="checkbox" checked={!!m.can_download} onChange={(e) => act(() => api(`/api/workspaces/${id}/members`, { body: { userId: m.id, canDownload: e.target.checked, canUpload: !!m.can_upload } }))} /> Download
              </label>
              <label className="row small">
                <input type="checkbox" checked={!!m.can_upload} onChange={(e) => act(() => api(`/api/workspaces/${id}/members`, { body: { userId: m.id, canDownload: !!m.can_download, canUpload: e.target.checked } }))} /> Upload
              </label>
              <button className="btn ghost sm" onClick={() => act(() => api(`/api/workspaces/${id}/members/${m.id}`, { method: 'DELETE' }))}>
                Remove
              </button>
            </div>
          ))}
          {nonMembers.length > 0 ? (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void act(() => api(`/api/workspaces/${id}/members`, { body: { userId: f.get('userId'), canDownload: true, canUpload: f.get('upload') === 'on' } }));
              }}
            >
              <select name="userId" aria-label="Person to add to workspace" className="field" style={{ flex: 1, minWidth: 160 }}>
                {nonMembers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.display_name} (@{p.username})
                  </option>
                ))}
              </select>
              <label className="row small">
                <input type="checkbox" name="upload" /> can upload
              </label>
              <button className="btn primary sm">Add member</button>
            </form>
          ) : (
            <div className="muted small">
              Everyone with an account is already a member. Create accounts under <a href="/?tab=people">People</a>.
            </div>
          )}
          <Activity id={id} />
          <div style={{ marginTop: 12 }}>
            {confirmDelete === 'workspace' ? (
              <button className="btn danger sm" onClick={() => act(() => api(`/api/workspaces/${id}`, { method: 'DELETE' })).then(() => (location.href = '/?tab=workspaces'))}>
                Confirm: move this {t.workspace} and its files to the trash
              </button>
            ) : (
              <button className="btn ghost sm" onClick={() => setConfirmDelete('workspace')}>
                Delete {t.workspace}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function People() {
  const [people, setPeople] = useState<Person[]>([]);
  const [shown, setShown] = useState<{ who: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => api<{ people: Person[] }>('/api/people').then((r) => setPeople(r.people), (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setError(null);
    try {
      const r = await api<{ username: string; displayName: string; temporaryPassword: string }>('/api/people', { body: { username: f.get('username'), displayName: f.get('displayName'), role: f.get('role') } });
      setShown({ who: `${r.displayName} (@${r.username})`, password: r.temporaryPassword });
      form.reset();
      void load();
    } catch (err) {
      setError((err as Error).message);
    }
  }
  return (
    <div className="list">
      <p className="muted small" style={{ margin: 0 }}>
        Accounts for people who work in {t.workspaces} between {t.meetings}. They choose their own password at first sign-in. {T.guests} do not need accounts.
      </p>
      {error && <div className="error">{error}</div>}
      {shown && (
        <div className="notice" data-testid="temp-password">
          Temporary password for {shown.who}: <code style={{ fontSize: 15 }}>{shown.password}</code> — pass it on privately; it is shown only once.{' '}
          <button className="btn sm" onClick={() => setShown(null)}>
            Done
          </button>
        </div>
      )}
      <form className="row" onSubmit={create}>
        <input name="displayName" className="field" style={{ flex: 1, minWidth: 140 }} placeholder="Display name" maxLength={60} required />
        <input name="username" className="field" style={{ flex: 1, minWidth: 120 }} placeholder="Username" required />
        <select name="role" className="field" style={{ width: 'auto' }} aria-label="Role" defaultValue="member" data-testid="person-role">
          <option value="member">Member: {t.workspaces} they are added to</option>
          <option value="host">Host: runs {t.meetings}, manages every {t.workspace}</option>
        </select>
        <button className="btn primary">Create account</button>
      </form>
      {people.map((p) => (
        <div key={p.id} className="list-item" data-testid="person-row">
          <div style={{ flex: 1, minWidth: 140 }}>
            {p.display_name} <span className="muted small">@{p.username}</span>
            {p.role === 'host' && <span className="tag" style={{ marginLeft: 6 }}>host</span>}
            {!p.enabled && <span className="tag" style={{ marginLeft: 6 }}>disabled</span>}
            {!!p.must_change_password && <span className="muted small"> · has not signed in yet</span>}
          </div>
          <button
            className="btn ghost sm"
            onClick={async () => {
              const r = await api<{ temporaryPassword: string }>(`/api/people/${p.id}`, { method: 'PATCH', body: { resetPassword: true } });
              setShown({ who: `${p.display_name} (@${p.username})`, password: r.temporaryPassword });
              void load();
            }}
          >
            Reset password
          </button>
          <button className="btn ghost sm" onClick={() => api(`/api/people/${p.id}`, { method: 'PATCH', body: { enabled: !p.enabled } }).then(load)}>
            {p.enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      ))}
    </div>
  );
}
